import { defineComponent, computed, ref, watch, SetupContext } from "@vue/composition-api";
import * as StudyAPI from "@/services/api/studies";
import { Study } from "@/psychlab/types/studies";
import { useTags } from "@/hooks/useTags";
import { useAssets } from "@/hooks/useAssets";
import { openRouterTab } from "@/utils/dom";
import { ContextOption } from "@/components.generic/context-menu";
import { useFavourites } from "@/hooks/useFavourites";
import * as AppModal from "@/AppModal";
import { Toggle } from "@/components.generic/scriptable-form";
// import { PaginationBase } from "@/components.generic/pagination-base";
import { columns } from "./table-config";
import { usePagination } from "./usePagination";
import { useUserPrefs } from "@/hooks/useUserPrefs";
import { useTranslate } from "@lang";
import { Pagination, Date as DisplayDate } from "@ui";

import { StatusBadge, StudySharing, BlueprintBadge, SessionCount } from "../vue";

import { usePhrases } from "./usePhrases";

import * as Utils from "./utils";


const loadQuerySort = (ctx:SetupContext) => {
	const v = Utils.getQueryValue<string|null>("sl_sort", ctx, null);
	if(!v){ return { desc:true, prop:"lastVisited" }; }
	const [
		p1,
		p2,
	] = v.split("-");
	if(!p2){ return { desc:false, prop:p1 }};
	return { desc: true, prop:p2 };
};

export const StudyList = defineComponent({
	props:{
		tag:{
			type:String
		},
	},
	components:{
		StatusBadge,
		StudySharing,
		BlueprintBadge,
		Toggle,
		Pagination,
		SessionCount,
		DisplayDate,
	},
	setup(props, context){

		const {
			total,
			currentPage,
			perPage,
			minLabel,
			maxLabel,
		} = usePagination({
			context,
			queryPrefix:"sl_",
		});

		const { translate } = useTranslate();
		
		const {
			studyListLimit
		} = useUserPrefs();

		const {
			studyCloseMessage,
			studyOpenMessage,
		} = usePhrases();

		const table = ref<any>();
		const filterBlueprints = ref<string[]>([]);
		const qe = Utils.getQueryValue("enabled", context, "1");
		const enabled = ref(qe === "1" ? true : false);
		const qs = loadQuerySort(context);

		const sortBy = ref(qs.prop);
		const sortDesc = ref(qs.desc);

		let initialPage = currentPage.value;

		// default limit
		perPage.value = studyListLimit.value;

		watch(enabled, v => {
			Utils.setQueryValue("enabled", v ? "1" : "0", context);
		});

		const loading = ref(false);

		const {
			isFavourite,
			addFavourite,
			removeFavourite
		} = useFavourites();

		const {
			getTaggedItems
		} = useTags();

		const {
			assets,
		} = useAssets();

		const studies = ref<Study[]>([]);

		const footerText = computed(() => {
			
			return translate("label.showingPagination")
			.replace("$x", minLabel.value.toString())
			.replace("$y", maxLabel.value.toString())
			.replace("$z", total.value.toString());

			// Showing 
			// 		{{ minLabel }}-{{ maxLabel }}
			// 		of {{total}}

		});

		const blueprints = computed(() => {
			if(!props.tag){
				return assets.value
				.filter(asset => asset.type === "Graph");
			}

			const tagged = getTaggedItems(props.tag);
			return assets.value
			.filter(asset => {
				return asset.type === "Graph" // is blueprint
				&& tagged.findIndex(ti => ti.item === asset._id) > -1; // is tagged
			});
		});

		const blueprintIds = computed(() => {
			if(filterBlueprints.value.length === 0){
				return blueprints.value.map(bp => bp._id);
			}
			return filterBlueprints.value;
		});

		const renameStudy = async (study:Study, name:string) => {
			try {
				await StudyAPI.setStudyName(study._id, name);
				study.name = name;
			}
			catch(err){
				console.log(err.message);
			}
			if(sortBy.value.includes("name")){
				refresh();
			}
		};

		const openContext = (e:any, study:Study) => {
			const options:ContextOption[] = [];
			options.push({
				icon:"mdi.home-analytics",
				name:translate("studies.action.openDashboard"),
				fn(){
					openStudyRoute(study._id);
				},
				order:-1,
			});
			options.push({
				icon:"mdi.pencil",
					name:translate("action.rename"),
					fn(){
						AppModal.editText({
							title:translate("studies.label.renameStudy"),
							value:study.name,
							validator(v){
								return v.length > 1 && v !== study.name;
							}
						}, (r) => {
							renameStudy(study, r);
						})
					},
			});

			if(!isFavourite(study._id)){
				options.push({
					icon:"mdi.star",
					name:translate("action.favourite"),
					fn(){
						addFavourite(study._id, "study");
					},
					order:3,
				});
			}
			else {
				options.push({
					icon:"mdi.star-outline",
					name:translate("action.unfavourite"),
					fn(){
						removeFavourite(study._id);
					},
					order:3,
				});
			}

			options.push({
				icon:study.enabled ? "mdi.toggle-switch-off-outline" : "mdi.toggle-switch",
				name:translate(`action.${study.enabled ? 'close' : 'open'}`),
				fn:() => toggleStudyEnabled(study),
			});
			AppModal.context(e, options);
		};

		const setSort = (prop:string) => {
			sortBy.value = prop;
		};

		const toggleStudyEnabled = (study:Study) => {
			// const title = `${study.enabled ? "Close" : "Open"} Study`;

			const title = translate(`studies.label.${study.enabled ? 'close' : 'open'}Study`)

			const description = study.enabled ? studyCloseMessage.value : studyOpenMessage.value;
			const variant = study.enabled ? "danger" : "info";
			AppModal.confirm({
				title,
				variant,
				okText:translate("prompts.action.understood"),
				description,
				cancelText:translate("prompts.action.cancel"),
			}, async () => {
				try {
					await StudyAPI.setStudyEnabled(study._id, !study.enabled);
				}
				catch(err){
					console.log(err.message);
				}
				refresh();
			});
		};

		const openStudyRoute = (studyId:string, tab?:boolean) => {
			if(tab){
				return openRouterTab(context.root.$router, "Run.Group", {
					groupId:studyId
				});
			}
			context.root.$router.push({
				name:"Run.Group",
				params:{
					groupId:studyId
				}
			});
		};

		const loadStudies = async(ctx:any) => {

			if(props.tag && blueprintIds.value.length === 0){
				return [];
			}
			const { currentPage, perPage, sortBy, sortDesc } = ctx;

			const page = currentPage;
			const limit = perPage;
			const sort = `${sortDesc ? '-' : ''}${sortBy}`;

			const fields = [
				"name",
				"target",
				"lastVisited",
				"created",
				"enabled",
				"description"
			];

			const q:any = {
				...{ limit, page, sort },
				select: fields.join(" ")
			};

			q["enabled"] = enabled.value;

			if(blueprintIds.value.length > 0){
				q["target.study[in]"] = blueprintIds.value.join(",");
			}
			loading.value = true;
			try {
				const { studies, count } = await StudyAPI.getStudiesPaginated(q);
				total.value = count;
				return studies;
			}
			catch(err){
				console.log(err.message);
			}

			loading.value = false;

			return [];
		};


		const onRefreshed = () => {
			if(initialPage >= 0){
				currentPage.value = initialPage;
				initialPage = -1;
			}
			context.emit("refreshed");
		};

		const tableConfig = computed(() => {
			return {
				fields:columns,
				provider:loadStudies
			}
		});

		const refresh = () => {
			if(table.value){
				table.value.refresh();
			}
		};

		const onSortChanged = (ctx:{ sortBy:string, sortDesc:boolean }) => {
			sortBy.value = ctx.sortBy;
			sortDesc.value = ctx.sortDesc;

			if(!sortBy.value){
				Utils.setQueryValue("sl_sort", undefined, context);
				return;
			}

			const sort = `${sortDesc.value ? '-' : ''}${sortBy.value}`;
			Utils.setQueryValue("sl_sort", sort, context);
		};

		const openDescription = (s:Study) => {
			// AppModal.editText({
			// 	value:s.description,
			// 	textarea:true,
			// 	size:"md"
			// }, (nd) => {
			// 	console.log(nd);
			// })

			const content = s.description;
			console.log(s);

			AppModal.renderRaw({
				title:s.name,
				content:content,
				okText:"Close",
				size:"lg",
				variant:"light"
			});
		};

		const getStudyTooltip = (s:Study) => {
			// return s.description;
			return "View description..."
		};

		watch([ perPage, currentPage, blueprintIds, enabled ], refresh);

		watch(perPage, () => {
			studyListLimit.value = perPage.value;
		})

		return {
			table,
			studies,
			blueprints,
			tableConfig,
			enabled,
			sortBy,
			minLabel,
			maxLabel,
			total,
			perPage,
			currentPage,
			sortDesc,
			loading,
			translate,
			getStudyTooltip,
			openDescription,
			onRefreshed,
			onSortChanged,
			openStudyRoute,
			openContext,
			setSort,
			refresh,
			footerText,
		};
	}
});